/**
 * Cache Configuration for Streameater
 * Central configuration for all caching strategies
 */

// Cache version - increment to invalidate all caches on deploy
export const CACHE_VERSION = "v2"; // Bumped to force 90s90s HipHop first in hero

// Time-To-Live values in milliseconds
export const TTL = {
  // Critical data - short TTL, frequently updated
  HERO: 5 * 60 * 1000, // 5 minutes
  TRENDING: 5 * 60 * 1000, // 5 minutes

  // Standard content - medium TTL
  HOME_DATA: 10 * 60 * 1000, // 10 minutes
  PODCAST_ROWS: 10 * 60 * 1000, // 10 minutes
  RADIO_ROWS: 10 * 60 * 1000, // 10 minutes

  // Stable content - longer TTL
  STATION_DETAILS: 30 * 60 * 1000, // 30 minutes
  PODCAST_EPISODES: 15 * 60 * 1000, // 15 minutes

  // User-specific - cached per session
  USER_FAVORITES: 5 * 60 * 1000, // 5 minutes
  USER_HISTORY: 2 * 60 * 1000, // 2 minutes

  // Static/rarely changing
  COUNTRIES: 24 * 60 * 60 * 1000, // 24 hours
  CATEGORIES: 24 * 60 * 60 * 1000, // 24 hours
} as const;

// Cache keys with type safety
export const CACHE_KEYS = {
  // Home data
  HOME_RADIO: (country: string) => `${CACHE_VERSION}:home:radio:${country}`,
  HOME_PODS: (country: string) => `${CACHE_VERSION}:home:pods:${country}`,

  // Hero sections
  HERO_RADIO: (country: string) => `${CACHE_VERSION}:hero:radio:${country}`,
  HERO_PODS: (country: string) => `${CACHE_VERSION}:hero:pods:${country}`,

  // Content rows
  RADIO_ROWS: (country: string) => `${CACHE_VERSION}:rows:radio:${country}`,
  PODCAST_ROWS: (country: string) => `${CACHE_VERSION}:rows:pods:${country}`,

  // Individual items
  STATION: (id: string) => `${CACHE_VERSION}:station:${id}`,
  PODCAST: (id: string) => `${CACHE_VERSION}:podcast:${id}`,
  PODCAST_EPISODES: (id: string) => `${CACHE_VERSION}:episodes:${id}`,

  // Lists
  TRENDING_RADIO: (country: string) =>
    `${CACHE_VERSION}:trending:radio:${country}`,
  TRENDING_PODS: (country: string) =>
    `${CACHE_VERSION}:trending:pods:${country}`,
  MOST_PLAYED: (country: string) => `${CACHE_VERSION}:mostplayed:${country}`,

  // Static
  COUNTRIES_LIST: `${CACHE_VERSION}:static:countries`,
  CATEGORIES_LIST: `${CACHE_VERSION}:static:categories`,
} as const;

// Priority levels for fetch queue
export type FetchPriority = "critical" | "high" | "medium" | "low";

export const PRIORITY_DELAYS: Record<FetchPriority, number> = {
  critical: 0, // Immediate
  high: 50, // 50ms delay
  medium: 200, // 200ms delay
  low: 500, // 500ms delay (or use requestIdleCallback)
};

// Memory cache limits
export const MEMORY_CACHE_CONFIG = {
  MAX_ITEMS: 100,
  MAX_SIZE_MB: 10,
};

// LocalStorage limits
export const LOCAL_STORAGE_CONFIG = {
  MAX_ITEMS: 50,
  CLEANUP_THRESHOLD: 0.9, // Cleanup when 90% full
};
